import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, BuildingStorefrontIcon } from '@heroicons/react/24/outline';
import { fetchMedicalStoreProfile } from '../../../services/MasterAdmin/medicalStoreService';
import { MedicalStoreProfile } from '../../../models/MasterAdmin/medicalStore';
import FilePreview from '../../../components/MasterAdmin/FilePreview';
import VendorDocumentsGrid from '../../../components/MasterAdmin/VendorDocumentsGrid';
import { VENDOR_TYPE } from '../../../services/vendorDocumentsService';

/** KYC files: VendorDocumentsGrid. Store photos: profile `photos` field only. */
type FileItem = { url: string; name: string };

const toSafeText = (value: unknown, fallback = 'N/A') => {
  if (value == null) return fallback;
  const text = String(value).trim();
  return text.length ? text : fallback;
};

const formatDateTime = (value: unknown): string => {
  if (!value) return 'N/A';
  const date = new Date(String(value));
  if (Number.isNaN(date.getTime())) return 'N/A';
  return date.toLocaleString();
};

const parseStringArray = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value
      .map((item) => (item == null ? '' : String(item).trim()))
      .filter((item) => item.length > 0);
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return [];
    if (trimmed.startsWith('[') && trimmed.endsWith(']')) {
      try {
        const parsed = JSON.parse(trimmed);
        return parseStringArray(parsed);
      } catch {
        return [trimmed];
      }
    }
    return trimmed
      .split(',')
      .map((item) => item.trim())
      .filter((item) => item.length > 0);
  }
  return [];
};

const parseFileArray = (value: unknown, prefix: string): FileItem[] => {
  const buildFromUnknown = (input: unknown): FileItem[] => {
    if (Array.isArray(input)) {
      return input
        .map((item, index) => {
          if (typeof item === 'string' && item.trim()) {
            return { url: item.trim(), name: `${prefix} ${index + 1}` };
          }
          if (item && typeof item === 'object') {
            const raw = item as Record<string, unknown>;
            const url = typeof raw.url === 'string' ? raw.url.trim() : '';
            if (!url) return null;
            const name = typeof raw.name === 'string' && raw.name.trim() ? raw.name.trim() : `${prefix} ${index + 1}`;
            return { url, name };
          }
          return null;
        })
        .filter((item): item is FileItem => !!item);
    }

    if (typeof input === 'string') {
      const trimmed = input.trim();
      if (!trimmed) return [];
      if (trimmed.startsWith('[') || trimmed.startsWith('{')) {
        try {
          const parsed = JSON.parse(trimmed);
          return buildFromUnknown(parsed);
        } catch {
          return [{ url: trimmed, name: `${prefix} 1` }];
        }
      }
      return [{ url: trimmed, name: `${prefix} 1` }];
    }

    if (input && typeof input === 'object') {
      const raw = input as Record<string, unknown>;
      const url = typeof raw.url === 'string' ? raw.url.trim() : '';
      if (!url) return [];
      const name = typeof raw.name === 'string' && raw.name.trim() ? raw.name.trim() : `${prefix} 1`;
      return [{ url, name }];
    }

    return [];
  };

  return buildFromUnknown(value);
};

const ValueRow: React.FC<{ label: string; value?: React.ReactNode }> = ({ label, value }) => (
  <div>
    <p className="text-sm text-gray-500 dark:text-gray-400">{label}</p>
    <div className="mt-1 text-sm font-medium text-gray-900 dark:text-white">{value ?? 'N/A'}</div>
  </div>
);

const MedicalStoreVendorDetailsPage: React.FC = () => {
  const { vendorId } = useParams<{ vendorId: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<MedicalStoreProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!vendorId) return;
    const load = async () => {
      try {
        setLoading(true);
        const res = await fetchMedicalStoreProfile(vendorId);
        setData(res);
      } catch {
        setError('Failed to load vendor details.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [vendorId]);

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[200px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="p-6 text-center">
        <p className="text-red-600 dark:text-red-400">{error || 'Vendor not found'}</p>
        <button
          type="button"
          onClick={() => navigate(`/vendors/medical-store/${vendorId}`)}
          className="mt-4 text-blue-600 dark:text-blue-400 hover:underline"
        >
          Back to vendor dashboard
        </button>
      </div>
    );
  }

  const photos = parseFileArray(data.photos, 'Store Photo');
  const storeDays = parseStringArray(data.storeDays);
  const medicineTypes = parseStringArray(data.medicineType);

  return (
    <div className="w-full min-w-0 max-w-6xl mx-auto p-3 sm:p-4 md:p-6">
      <div className="mb-6 flex items-center justify-between">
        <button
          type="button"
          onClick={() => navigate(`/vendors/medical-store/${vendorId}`)}
          className="flex items-center text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
        >
          <ArrowLeftIcon className="h-5 w-5 mr-2" />
          Back to vendor dashboard
        </button>
        <button
          type="button"
          onClick={() => navigate(`/monitoring?tab=intelligence&vendorId=${vendorId}&category=medical_store`)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs sm:text-sm font-semibold rounded-lg bg-gradient-to-r from-blue-600 to-cyan-600 text-white hover:from-blue-700 hover:to-cyan-700 active:scale-95 transition-all shadow-sm border border-blue-500/20"
        >
          <svg
            className="h-4 w-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z"
            />
          </svg>
          Analyze ML Intelligence
        </button>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
        <div className="px-6 py-5 border-b dark:border-gray-700 flex items-center gap-3">
          <div className="p-2 bg-blue-50 dark:bg-blue-900/30 rounded-lg">
            <BuildingStorefrontIcon className="h-8 w-8 text-blue-600 dark:text-blue-400" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900 dark:text-white">{data.name}</h1>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
              {toSafeText(data.address)}, {toSafeText(data.city)}, {toSafeText(data.state)} - {toSafeText(data.pincode)}
            </p>
            <div className="mt-2 flex flex-wrap gap-2">
              <span
                className={`inline-block px-2 py-0.5 text-xs font-semibold rounded-full ${
                  data.Vendor.status === 'approved'
                    ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                    : data.Vendor.status === 'pending'
                    ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300'
                    : 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300'
                }`}
              >
                {data.Vendor.status.charAt(0).toUpperCase() + data.Vendor.status.slice(1)}
              </span>
              <span
                className={`inline-block px-2 py-0.5 text-xs font-semibold rounded-full ${
                  data.Vendor.isActive
                    ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                    : 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300'
                }`}
              >
                {data.Vendor.isActive ? 'Active' : 'Inactive'}
              </span>
              <span
                className={`inline-block px-2 py-0.5 text-xs font-semibold rounded-full ${
                  data.Vendor.isTermsAccepted
                    ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-300'
                    : 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300'
                }`}
              >
                {data.Vendor.isTermsAccepted ? 'Terms Accepted' : 'Terms Not Accepted'}
              </span>
            </div>
            <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
              Terms accepted at: {formatDateTime(data.Vendor.termsAcceptedAt)}
            </p>
          </div>
        </div>

        <div className="p-6 space-y-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="space-y-4">
              <h2 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wider border-b dark:border-gray-700 pb-2">
                Basic Information
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <ValueRow label="Store Name" value={toSafeText(data.name)} />
                <ValueRow label="Owner Name" value={toSafeText(data.ownerName)} />
                <ValueRow label="Phone" value={toSafeText(data.contactNumber)} />
                <ValueRow label="Email" value={toSafeText(data.emailId)} />
                <ValueRow label="Floor" value={toSafeText(data.floor)} />
                <ValueRow label="Store Timing" value={toSafeText(data.storeTiming)} />
              </div>
            </div>

            <div className="space-y-4">
              <h2 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wider border-b dark:border-gray-700 pb-2">
                Address Information
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <ValueRow label="Address" value={toSafeText(data.address)} />
                <ValueRow label="Landmark" value={toSafeText(data.landmark)} />
                <ValueRow label="City" value={toSafeText(data.city)} />
                <ValueRow label="State" value={toSafeText(data.state)} />
                <ValueRow label="Pincode" value={toSafeText(data.pincode)} />
                <ValueRow label="Map Location" value={toSafeText(data.location)} />
              </div>
            </div>

            <div className="space-y-4">
              <h2 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wider border-b dark:border-gray-700 pb-2">
                Business Information
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <ValueRow label="License Number" value={toSafeText(data.licenseNumber)} />
                <ValueRow label="GST Number" value={toSafeText(data.gstNumber)} />
                <ValueRow label="PAN Number" value={toSafeText(data.panNumber)} />
                <ValueRow label="Vendor ID" value={toSafeText(data.vendorId)} />
                <ValueRow
                  label="Store Days"
                  value={storeDays.length ? storeDays.join(', ') : toSafeText(data.storeDays)}
                />
              </div>
            </div>

            <div className="space-y-4">
              <h2 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wider border-b dark:border-gray-700 pb-2">
                Features
              </h2>
              <div className="space-y-3">
                <ValueRow
                  label="Medicine Type"
                  value={medicineTypes.length ? medicineTypes.join(', ') : toSafeText(data.medicineType)}
                />
                <div className="flex flex-wrap gap-2">
                  {data.isRareMedicationsAvailable && (
                    <span className="px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 rounded-full text-sm">
                      Rare Medications
                    </span>
                  )}
                  {data.isOnlinePayment && (
                    <span className="px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 rounded-full text-sm">
                      Online Payment
                    </span>
                  )}
                  {data.isLiftAccess && (
                    <span className="px-2 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300 rounded-full text-sm">
                      Lift Access
                    </span>
                  )}
                  {data.isWheelchairAccess && (
                    <span className="px-2 py-1 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300 rounded-full text-sm">
                      Wheelchair Access
                    </span>
                  )}
                  {data.isParkingAvailable && (
                    <span className="px-2 py-1 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-800 dark:text-indigo-300 rounded-full text-sm">
                      Parking Available
                    </span>
                  )}
                  {!data.isRareMedicationsAvailable &&
                    !data.isOnlinePayment &&
                    !data.isLiftAccess &&
                    !data.isWheelchairAccess &&
                    !data.isParkingAvailable && (
                      <span className="text-sm text-gray-500 dark:text-gray-400">No additional features provided.</span>
                    )}
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <h2 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wider border-b dark:border-gray-700 pb-2">
              Vendor documents
            </h2>
            <p className="text-xs text-gray-500 dark:text-gray-400 -mt-2 mb-2">
              Files registered for verification (labels match the document name stored for this vendor).
            </p>
            {vendorId && (
              <VendorDocumentsGrid vendorId={vendorId} vendorType={VENDOR_TYPE.MEDICAL_STORE} className="mb-8" />
            )}
          </div>

          <div className="space-y-4">
            <h2 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wider border-b dark:border-gray-700 pb-2">
              Photos
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {photos.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400 col-span-full">No store photos uploaded.</p>
              ) : (
                photos.map((photo, index) => (
                  <FilePreview key={`${photo.url}-${index}`} url={photo.url} name={photo.name} className="w-full" />
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MedicalStoreVendorDetailsPage;
